import type { FC, ReactNode } from 'react'
import type { SessionId } from '@/types'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { toWibClock } from '@/lib/dates'
import { buttonVariants } from '@/components/ui/button'
import { SectionHeading } from '@/components/generals/section-heading'
import { BookButton } from '@/components/landing/booking-provider'

import {
    Info,
    MessageCircle,
    Smile,
    Stethoscope,
    Sun,
    Sunset
} from 'lucide-react'

import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger
} from '@/components/ui/tabs'

import {
    clinic,
    generalDoctors,
    scheduleDays,
    sessionOrder,
    sessions,
    whatsappLink
} from '@/constants/clinic'

interface SessionChipProps {
    session: SessionId
    className?: string
}

const SessionChip: FC<SessionChipProps> = ({ session, className }): ReactNode => {
    const Icon = session === 'pagi' ? Sun : Sunset

    return (
        <span
            className={cn(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset',
                session === 'pagi'
                    ? 'bg-brand-50 text-brand-800 ring-brand-200'
                    : 'bg-sky-50 text-sky-800 ring-sky-200',
                className
            )}
        >
            <Icon className='size-3.5' aria-hidden='true' />
            {sessions[session].label}
        </span>
    )
}

const DoctorAvatar: FC<{ initials: string }> = ({ initials }): ReactNode => {
    return (
        <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-brand-50 to-brand-100 text-xs font-bold text-brand-800 ring-1 ring-brand-200/70' aria-hidden='true'>
            {initials}
        </span>
    )
}

/** Index of today in scheduleDays (0 = Senin), or null on Minggu. Browser only. */
const getTodayIndex = (): number | null => {
    const weekday = toWibClock(new Date()).getUTCDay()

    return weekday === 0 ? null : weekday - 1
}

const GeneralSchedule: FC = (): ReactNode => {
    const [today, setToday] = useState<number | null>(null)
    const [dayIndex, setDayIndex] = useState(0)

    useEffect(() => {
        const index = getTodayIndex()

        setToday(index)
        if (index !== null) setDayIndex(index)
    }, [])

    return (
        <>
            <div className='hidden overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-border md:block'>
                <table className='w-full border-collapse text-sm'>
                    <caption className='sr-only'>
                        Jadwal praktik dokter Poli Umum, Senin sampai Sabtu
                    </caption>

                    <thead className='bg-muted/70'>
                        <tr>
                            <th scope='col' className='px-6 py-4 text-left font-semibold text-muted-foreground'>
                                Dokter
                            </th>

                            {scheduleDays.map((day, index) => (
                                <th
                                    key={day.short}
                                    scope='col'
                                    className={cn(
                                        'px-2 py-4 text-center font-semibold',
                                        today === index ? 'bg-brand-50 text-brand-800' : 'text-muted-foreground'
                                    )}
                                >
                                    <abbr title={day.long} className='no-underline'>
                                        {day.short}
                                    </abbr>

                                    {today === index && (
                                        <span className='mt-1 block text-[10px] font-bold tracking-wider text-brand-600 uppercase'>
                                            Hari ini
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className='divide-y divide-border'>
                        {generalDoctors.map(doctor => (
                            <tr key={doctor.name} className='transition-colors hover:bg-muted/30'>
                                <th scope='row' className='px-6 py-3.5 text-left font-semibold text-ink'>
                                    <span className='flex items-center gap-3'>
                                        <DoctorAvatar initials={doctor.initials} />
                                        {doctor.name}
                                    </span>
                                </th>

                                {doctor.schedule.map((session, index) => (
                                    <td
                                        key={scheduleDays[index].short}
                                        className={cn('px-2 py-3.5 text-center', today === index && 'bg-brand-50/50')}
                                    >
                                        {session ? (
                                            <SessionChip session={session} />
                                        ) : (
                                            <>
                                                <span className='text-slate-300' aria-hidden='true'>
                                                    –
                                                </span>

                                                <span className='sr-only'>
                                                    Tidak praktik
                                                </span>
                                            </>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='md:hidden'>
                <div className='-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar' role='group' aria-label='Pilih hari praktik'>
                    {scheduleDays.map((day, index) => (
                        <button
                            key={day.short}
                            type='button'
                            aria-pressed={dayIndex === index}
                            onClick={() => setDayIndex(index)}
                            className={cn(
                                'flex shrink-0 flex-col items-center rounded-2xl border px-4 py-2 text-sm font-semibold transition-all outline-none focus-visible:ring-4 focus-visible:ring-ring/30',
                                dayIndex === index
                                    ? 'border-brand-600 bg-brand-600 text-white shadow-md shadow-brand-700/25'
                                    : 'border-border bg-white text-ink'
                            )}
                        >
                            {day.long}

                            <span className={cn('text-[10px] font-bold tracking-wider uppercase', today === index ? (dayIndex === index ? 'text-brand-100' : 'text-brand-600') : 'invisible')}>
                                Hari ini
                            </span>
                        </button>
                    ))}
                </div>

                <div className='mt-4 grid gap-3'>
                    {sessionOrder.map(session => {
                        const doctors = generalDoctors.filter(doctor => doctor.schedule[dayIndex] === session)

                        return (
                            <div key={session} className='rounded-3xl bg-white p-5 shadow-sm ring-1 ring-border'>
                                <div className='flex items-center justify-between gap-3'>
                                    <SessionChip session={session} />

                                    <span className='text-xs text-muted-foreground'>
                                        Praktik mulai
                                        {' '}
                                        {sessions[session].practice}
                                        {' '}
                                        WIB
                                    </span>
                                </div>

                                {doctors.length > 0 ? (
                                    <ul className='mt-4 grid gap-3'>
                                        {doctors.map(doctor => (
                                            <li key={doctor.name} className='flex items-center gap-3 text-sm font-semibold text-ink'>
                                                <DoctorAvatar initials={doctor.initials} />
                                                {doctor.name}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className='mt-4 text-sm text-muted-foreground'>
                                        Tidak ada dokter Poli Umum yang praktik pada sesi ini.
                                    </p>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </>
    )
}

const DentalSchedule: FC = (): ReactNode => {
    return (
        <div className='grid gap-6 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-border sm:p-8 lg:grid-cols-[1fr_auto] lg:items-center'>
            <div className='flex items-start gap-4'>
                <span className='flex size-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ring-1 ring-brand-100'>
                    <Smile className='size-6' aria-hidden='true' />
                </span>

                <div>
                    <h3 className='text-lg font-bold text-ink'>
                        Jadwal dokter gigi
                    </h3>

                    <p className='mt-1.5 max-w-xl text-[15px] leading-relaxed text-muted-foreground'>
                        Dokter gigi yang bertugas dapat ditanyakan langsung ke klinik. Sesi Poli Gigi yang masih tersedia bisa dilihat di bagian ketersediaan janji, lalu dipesan secara online.
                    </p>
                </div>
            </div>

            <div className='flex flex-col gap-2 sm:flex-row'>
                <BookButton selection={{ service: 'poli-gigi' }}>
                    Buat janji Poli Gigi
                </BookButton>

                <a
                    href={whatsappLink('Halo Klinik Sehat Budi Luhur, saya ingin menanyakan jadwal dokter Poli Gigi.')}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={buttonVariants({ variant: 'outline' })}
                >
                    <MessageCircle className='size-4 text-brand-600' aria-hidden='true' />
                    Tanya via WhatsApp
                </a>
            </div>
        </div>
    )
}

const ScheduleSection: FC = (): ReactNode => {
    return (
        <section id='jadwal-dokter' aria-labelledby='jadwal-dokter-title' className='bg-white py-20 sm:py-24'>
            <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                <SectionHeading
                    id='jadwal-dokter-title'
                    eyebrow='Jadwal'
                    title='Jadwal dokter'
                    description='Jadwal praktik mingguan. Praktik pagi dimulai pukul 08.00 WIB dan praktik sore pukul 16.00 WIB.'
                />

                <Tabs defaultValue='poli-umum' className='mt-10'>
                    <TabsList aria-label='Pilih poli'>
                        <TabsTrigger value='poli-umum'>
                            <Stethoscope aria-hidden='true' />
                            Poli Umum
                        </TabsTrigger>

                        <TabsTrigger value='poli-gigi'>
                            <Smile aria-hidden='true' />
                            Poli Gigi
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value='poli-umum'>
                        <GeneralSchedule />
                    </TabsContent>

                    <TabsContent value='poli-gigi'>
                        <DentalSchedule />
                    </TabsContent>
                </Tabs>

                <div className='mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground'>
                    <span className='inline-flex items-center gap-2'>
                        <SessionChip session='pagi' />
                        Pendaftaran
                        {' '}
                        {sessions.pagi.registration}
                        , praktik mulai
                        {' '}
                        {sessions.pagi.practice}
                    </span>

                    <span className='inline-flex items-center gap-2'>
                        <SessionChip session='sore' />
                        Pendaftaran
                        {' '}
                        {sessions.sore.registration}
                        , praktik mulai
                        {' '}
                        {sessions.sore.practice}
                    </span>

                    <span>
                        Hari Minggu poli tutup.
                    </span>
                </div>

                <div className='mt-6 flex items-start gap-3 rounded-2xl bg-sky-50 p-4 text-sm ring-1 ring-sky-100 ring-inset sm:p-5'>
                    <Info className='mt-0.5 size-5 shrink-0 text-sky-700' aria-hidden='true' />

                    <div>
                        <p className='font-semibold text-sky-900'>
                            Jadwal KIA/KB, USG kehamilan, dan layanan lain
                        </p>

                        <p className='mt-1 text-sky-900/80'>
                            Hubungi klinik di
                            {' '}
                            <a href={clinic.phone.href} className='font-semibold whitespace-nowrap underline underline-offset-2'>
                                {clinic.phone.label}
                            </a>
                            {' '}
                            atau WhatsApp
                            {' '}
                            <a
                                href={clinic.whatsapp.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='font-semibold whitespace-nowrap underline underline-offset-2'
                            >
                                {clinic.whatsapp.label}
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default ScheduleSection
